import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DebtorEditor from "@/components/DebtorEditor";
import { X, Upload, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

const F = ({ label, value, onChange, testId }) => (
  <div>
    <label className="kicker block mb-1">{label}</label>
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
  </div>
);

export default function SozlesmeDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);
  const [yetkililer, setYetkililer] = useState([]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) api.get("/cari/yetkili-options").then((r) => setYetkililer(r.data || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const extract = async () => {
    if (!file) { toast.error("Sözleşme dosyası seçin"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cari/sozlesme-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setD({ ...data, alis_fiyati: "", vadeler: data.vadeler || [], borclular: data.borclular || [] });
      toast.success("Sözleşme okundu, bilgileri kontrol edin");
    } catch (e) { toast.error(e.response?.data?.detail || "Sözleşme okunamadı"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!d?.musteri) { toast.error("Müşteri adı gerekli"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cari/sozlesme-create", {
        musteri: d.musteri, tc_no: d.tc_no, adres: d.adres, telefon: d.telefon,
        borclular: (d.borclular || []).filter((b) => (b.ad || "").trim()).map((b) => ({ ad: b.ad, tc: b.tc || "" })),
        urun: d.urun, satis_fiyati: Number(d.satis_fiyati) || 0,
        alis_fiyati: Number(d.alis_fiyati) || 0, pesinat: Number(d.pesinat) || 0,
        yetkili: d.yetkili || "",
        vadeler: (d.vadeler || []).map((v) => ({ vade_tarihi: v.vade_tarihi, tutar: Number(v.tutar) || 0 })),
        senet: d.senet || null,
      });
      toast.success(`${d.musteri} — ${data.created} vade oluşturuldu`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Oluşturulamadı"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sozlesme-dialog">
      <div className="bg-white rounded-sm border border-[#E7DECC] w-full max-w-2xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7DECC] sticky top-0 bg-white">
          <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#7C5E9B]" /> Sözleşmeden Cari Oluştur</h2>
          <button onClick={onClose} data-testid="sozlesme-close" className="p-1 hover:bg-[#EFE7D6] rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!d && (
            <>
              <p className="text-sm text-[#8C8377]">Sözleşme görselini (JPG/PNG) veya PDF'ini yükleyin; sistem müşteri bilgilerini ve vadeleri otomatik okusun. Sadece <b>Alış Fiyatı</b>'nı siz gireceksiniz.</p>
              <input type="file" accept=".jpg,.jpeg,.png,.pdf" data-testid="sozlesme-file" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm text-[#8C8377] file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#EFE7D6] file:text-sm file:font-medium file:cursor-pointer hover:file:bg-[#E7DECC]" />
            </>
          )}
          {d && (
            <div className="space-y-4" data-testid="sozlesme-preview">
              <div className="grid grid-cols-2 gap-3">
                <F label="MÜŞTERİ (1. BORÇLU)" value={d.musteri} onChange={(v) => set("musteri", v)} testId="soz-musteri" />
                <F label="TC NO" value={d.tc_no} onChange={(v) => set("tc_no", v)} testId="soz-tc" />
                <F label="TELEFON" value={d.telefon} onChange={(v) => set("telefon", v)} testId="soz-tel" />
                <F label="ÜRÜN" value={d.urun} onChange={(v) => set("urun", v)} testId="soz-urun" />
              </div>
              <div className="border border-[#E7DECC] rounded-sm p-3">
                <DebtorEditor borclular={d.borclular || []} onChange={(list) => set("borclular", list)} />
              </div>
              <F label="ADRES" value={d.adres} onChange={(v) => set("adres", v)} testId="soz-adres" />
              {d.senet_preview && (
                <div className="border border-[#2F6E4E]/40 bg-[#2F6E4E]/5 rounded-sm p-3" data-testid="soz-senet-preview">
                  <div className="kicker mb-2 flex items-center gap-1.5 text-[#2F6E4E]"><CheckCircle2 className="w-3.5 h-3.5" /> OTOMATİK AYIKLANAN SENET</div>
                  <img src={d.senet_preview} alt="Senet" className="max-h-56 w-auto rounded-sm border border-[#E7DECC]" />
                  <p className="text-xs text-[#8C8377] mt-1.5">Bu senet görseli Cari föyüne (ve İcra'ya aktarılınca İcra föyüne) otomatik eklenecek. Hatalıysa bu sözleşme için senet kaydedilmez.</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <F label="SATIŞ FİYATI (SF)" value={d.satis_fiyati} onChange={(v) => set("satis_fiyati", v)} testId="soz-sf" />
                <div>
                  <label className="kicker block mb-1 text-[#B8933F]">ALIŞ FİYATI (AF) — MANUEL</label>
                  <input value={d.alis_fiyati ?? ""} onChange={(e) => set("alis_fiyati", e.target.value)} data-testid="soz-af" placeholder="Elle girin" className="w-full px-3 py-2 border border-[#B8933F] rounded-sm text-sm focus:outline-none" />
                </div>
                <F label="PEŞİNAT" value={d.pesinat} onChange={(v) => set("pesinat", v)} testId="soz-pesinat" />
              </div>
              <div>
                <label className="kicker block mb-1 text-[#B8933F]">YETKİLİ SEÇ</label>
                <select
                  value={d.yetkili || ""}
                  onChange={(e) => set("yetkili", e.target.value)}
                  data-testid="soz-yetkili"
                  className="w-full px-3 py-2 border border-[#B8933F] rounded-sm text-sm bg-white focus:outline-none focus:border-[#B8933F]"
                >
                  <option value="">— Yetkili seçin —</option>
                  {yetkililer.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <div className="kicker mb-2">VADELER ({(d.vadeler || []).length})</div>
                <div className="border border-[#E7DECC] rounded-sm max-h-48 overflow-y-auto">
                  {(d.vadeler || []).map((v, i) => (
                    <div key={i} className="flex gap-2 items-center px-2 py-1.5 border-b border-[#E7DECC] last:border-0">
                      <input value={v.vade_tarihi ?? ""} onChange={(e) => { const n = [...d.vadeler]; n[i] = { ...n[i], vade_tarihi: e.target.value }; set("vadeler", n); }} className="w-36 px-2 py-1 border border-[#E7DECC] rounded-sm text-sm" data-testid={`soz-vade-tarih-${i}`} />
                      <input value={v.tutar ?? ""} onChange={(e) => { const n = [...d.vadeler]; n[i] = { ...n[i], tutar: e.target.value }; set("vadeler", n); }} className="w-32 px-2 py-1 border border-[#E7DECC] rounded-sm text-sm text-right" data-testid={`soz-vade-tutar-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E7DECC] sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">İptal</button>
          {!d ? (
            <button onClick={extract} disabled={busy || !file} data-testid="sozlesme-extract-btn" className="flex items-center gap-2 px-4 py-2 bg-[#7C5E9B] hover:bg-[#9A3FC7] text-white rounded-sm text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Sözleşmeyi Oku
            </button>
          ) : (
            <button onClick={create} disabled={busy} data-testid="sozlesme-create-btn" className="flex items-center gap-2 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Cari Oluştur
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
